import { useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    LinearProgress
} from '@mui/material'
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon
} from '@mui/icons-material'
import { useProducts, useProductManagement } from '../../hooks/useProducts'
import { formatCurrency } from '../../utils'
import toast from 'react-hot-toast'

export default function ProductsPage() {
    const [search, setSearch] = useState('')
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingProduct, setEditingProduct] = useState<any>(null)
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        indetailDescription: '',
        shortDescription: '',
        price: '',
        originalPrice: '',
        category: { main: 'solar-panels', sub: '' },
        specifications: { brand: '', model: '', warranty: '5 years' },
        status: 'active' as const,
        moq: '',
        discountOnMoq: '',
        videoUrl: '',
        imageUrl: '', // Added imageUrl field
        useCase: '',
        benefits: '',
        features: '',
        specsText: ''
    })

    const { products, loading } = useProducts({ search })
    const { createProduct, updateProduct, deleteProduct, loading: mutating } = useProductManagement()

    const handleOpenDialog = (product?: any) => {
        if (product) {
            setEditingProduct(product)
            setFormData({
                name: product.name,
                description: product.description || '',
                indetailDescription: product.indetailDescription || '',
                shortDescription: product.shortDescription || '',
                price: product.price?.toString() || '',
                originalPrice: product.originalPrice?.toString() || '',
                category: typeof product.category === 'string' ? { main: product.category, sub: '' } : (product.category || { main: '', sub: '' }),
                specifications: typeof product.specifications === 'string' ? { brand: '', model: '', warranty: '' } : (product.specifications || { brand: '', model: '', warranty: '' }),
                status: product.status || 'active',
                moq: product.moq?.toString() || '',
                discountOnMoq: product.discountOnMoq?.toString() || '',
                videoUrl: product.videoUrl || '',
                imageUrl: product.media?.images?.[0] || '', // Populate from existing
                useCase: product.useCase || '',
                benefits: product.benefits || '',
                features: Array.isArray(product.features) ? product.features.join('\n') : (product.features || ''),
                specsText: typeof product.specifications === 'string' ? product.specifications : ''
            })
        } else {
            setEditingProduct(null)
            setFormData({
                name: '',
                description: '',
                indetailDescription: '',
                shortDescription: '',
                price: '',
                originalPrice: '',
                category: { main: 'solar-panels', sub: '' },
                specifications: { brand: '', model: '', warranty: '5 years' },
                status: 'active',
                moq: '',
                discountOnMoq: '',
                videoUrl: '',
                imageUrl: '',
                useCase: '',
                benefits: '',
                features: '',
                specsText: ''
            })
        }
        setDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setEditingProduct(null)
    }

    const handleSubmit = async () => {
        try {
            // Determine category value
            const categoryVal = formData.category;

            // Use provided image URL or fallback
            const finalImage = formData.imageUrl.trim() || 'https://via.placeholder.com/300';

            const productData = {
                name: formData.name,
                slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
                description: formData.description,
                indetailDescription: formData.indetailDescription,
                shortDescription: formData.shortDescription,
                price: parseFloat(formData.price) || 0,
                originalPrice: parseFloat(formData.originalPrice) || parseFloat(formData.price) || 0,
                discount: 0,
                moq: parseInt(formData.moq) || 1,
                discountOnMoq: parseFloat(formData.discountOnMoq) || 0,
                features: formData.features.split('\n').filter(f => f.trim()),
                category: categoryVal,
                specifications: formData.specsText ? formData.specsText : formData.specifications,
                videoUrl: formData.videoUrl,
                useCase: formData.useCase,
                benefits: formData.benefits,
                media: { images: [finalImage] }, // Use the state value
                inventory: { quantity: 100, reserved: 0, available: 100 },
                seo: { metaTitle: formData.name, metaDescription: formData.shortDescription, keywords: [] },
                status: formData.status
            }

            if (editingProduct) {
                await updateProduct(editingProduct.id, productData as any)
                toast.success('Product updated successfully')
            } else {
                await createProduct(productData as any)
                toast.success('Product created successfully')
            }
            handleCloseDialog()
        } catch (error) {
            toast.error('Failed to save product')
            console.error(error)
        }
    }

    const handleDelete = async (productId: string) => {
        if (confirm('Are you sure you want to delete this product?')) {
            try {
                await deleteProduct(productId)
                toast.success('Product deleted successfully')
            } catch (error) {
                toast.error('Failed to delete product')
            }
        }
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Products ({products.length})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    }}
                >
                    Add Product
                </Button>
            </Box>

            {/* Search */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Search products..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'grey.500' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </CardContent>
            </Card>

            {/* Products Table */}
            <Card>
                <CardContent>
                    {loading ? (
                        <LinearProgress />
                    ) : products.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 4 }}>
                            No products found
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Price</TableCell>
                                        <TableCell>MOQ</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.map((product) => (
                                        <TableRow key={product.id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box
                                                        component="img"
                                                        src={product.media?.images?.[0] || 'https://via.placeholder.com/50'}
                                                        alt={product.name}
                                                        sx={{ width: 50, height: 50, borderRadius: 1, objectFit: 'cover' }}
                                                    />
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {product.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: 'grey.500' }}>
                                                            {typeof product.specifications === 'object' ? product.specifications.brand : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={typeof product.category === 'object' ? product.category.main : product.category}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {formatCurrency(product.price)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{(product as any).moq || 1}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={product.status}
                                                    color={product.status === 'active' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => handleOpenDialog(product)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(product.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Product Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Category (Main)"
                                value={formData.category.main}
                                onChange={(e) => setFormData({ ...formData, category: { ...formData.category, main: e.target.value } })}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Price (PKR)"
                                type="number"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Original Price (Before Discount)"
                                type="number"
                                value={formData.originalPrice}
                                onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth
                                label="MOQ"
                                type="number"
                                value={formData.moq}
                                onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth
                                label="Disc. on MOQ"
                                type="number"
                                value={formData.discountOnMoq}
                                onChange={(e) => setFormData({ ...formData, discountOnMoq: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Short Description"
                                value={formData.shortDescription}
                                onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Detailed Description"
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Indetail Description (Extended)"
                                multiline
                                rows={4}
                                value={formData.indetailDescription}
                                onChange={(e) => setFormData({ ...formData, indetailDescription: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Brand"
                                value={formData.specifications.brand}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    specifications: { ...formData.specifications, brand: e.target.value }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Model"
                                value={formData.specifications.model}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    specifications: { ...formData.specifications, model: e.target.value }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Video URL"
                                value={formData.videoUrl}
                                onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextField
                                fullWidth
                                label="Image URL (Direct Link)"
                                placeholder="https://example.com/image.jpg"
                                value={formData.imageUrl}
                                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                                helperText="Enter a direct link to an image (e.g. from Unsplash or other hosting)"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Use Case"
                                multiline
                                rows={2}
                                value={formData.useCase}
                                onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Benefits"
                                multiline
                                rows={2}
                                value={formData.benefits}
                                onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Features (One per line)"
                                multiline
                                rows={3}
                                value={formData.features}
                                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={mutating || !formData.name || !formData.price}
                    >
                        {editingProduct ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
